/** Extrai texto de note FHIR (objeto ou array). */
export function extractNoteText(note: any): string {
  if (!note) return "";
  if (Array.isArray(note)) return note?.[0]?.text || "";
  if (typeof note === "string") return note;
  return note?.text || "";
}

/**
 * Posologia (`form`) para PDF — alinhada a PrescriptionsActions.generateItem
 * e PrescriptionCare (quantity + forma + intervalo + duração).
 * Evita duplicar unidade (não junta doseQuantity.unit no texto da forma).
 * Sem dosageInstruction → string vazia (não mostra "Modo de Usar" no simples).
 */
export function buildMedicationPosologyForm(medicationRequest: any): string {
  const dosage = medicationRequest?.dosageInstruction?.[0];
  if (!dosage) return "";

  const doseAndRate = dosage?.doseAndRate?.[0];
  const timing = dosage?.timing?.repeat;
  const quantityValue = doseAndRate?.doseQuantity?.value;
  const typeText = doseAndRate?.type?.text;

  const parts = [
    quantityValue !== undefined &&
      quantityValue !== null &&
      `${quantityValue}`,
    typeText && `${typeText}`,
    // Espelha generateItem: gated em frequency; AuxiliarData grava frequency: 1
    timing?.frequency &&
      timing?.period != null &&
      `a cada ${timing.period} h`,
    timing?.duration != null
      ? `por ${timing.duration} dias`
      : timing?.period != null
        ? "por tempo indeterminado"
        : undefined,
  ];

  return parts.filter((p): p is string => !!p).join(" ");
}

/** Coding de reason/condition (objeto legado ou array FHIR). */
export function extractReasonCoding(reason: any) {
  if (!reason) return undefined;
  if (Array.isArray(reason)) {
    return reason?.[0]?.concept?.coding?.[0];
  }
  return reason?.concept?.coding?.[0];
}

/** ServiceRequest de procedimento: category com text "procedure" (AuxiliarData). */
export function isProcedureServiceRequest(serviceRequest: any): boolean {
  return !!serviceRequest?.category?.some((c: any) => c?.text === "procedure");
}

/**
 * DocumentReference.context pode vir como array (XML multi-context),
 * objeto com reference, ou Backbone com encounter[].
 */
export function documentBelongsToEncounter(
  documentReference: any,
  encounterId: string
): boolean {
  if (!encounterId || !documentReference?.context) return false;
  const ctx = documentReference.context;

  if (Array.isArray(ctx)) {
    return ctx.some(
      (c: any) =>
        c?.reference?.includes(encounterId) ||
        c?.encounter?.some?.((e: any) => e?.reference?.includes(encounterId))
    );
  }

  if (ctx?.reference?.includes(encounterId)) return true;
  if (
    ctx?.encounter?.some?.((e: any) => e?.reference?.includes(encounterId))
  ) {
    return true;
  }

  try {
    return JSON.stringify(ctx).includes(encounterId);
  } catch {
    return false;
  }
}

/** Documento assinado: securityLabel ProofOfOrigin (padrão ehrrecords). */
export function isDocumentSigned(documentReference: any): boolean {
  return (
    documentReference?.securityLabel?.[0]?.coding?.[0]?.code ===
    "ProofOfOrigin"
  );
}

/**
 * FHIR JSON pode serializar basedOn como objeto único (1 ref) ou array.
 * Sempre normaliza para lista antes de .find/.map.
 */
export function asBasedOnList(basedOn: any): Array<{ reference?: string }> {
  if (!basedOn) return [];
  return Array.isArray(basedOn) ? basedOn : [basedOn];
}

/** IDs referenciados em basedOn (MedicationRequest/xxx ou ServiceRequest/xxx). */
export function extractBasedOnResourceIds(documentReference: any): string[] {
  return asBasedOnList(documentReference?.basedOn)
    .map((b: any) => {
      const ref = b?.reference;
      if (!ref || typeof ref !== "string") return undefined;
      // Remove /_history/... e pega o id após ResourceType/
      const withoutHistory = ref.split("/_history")[0];
      const parts = withoutHistory.split("/").filter(Boolean);
      return parts[parts.length - 1] || undefined;
    })
    .filter(Boolean) as string[];
}
